/**
 * The lower half of the landing page: how it works, proof, sample report,
 * trust, FAQ, closing CTA and footer.
 *
 * Split out of Landing.tsx because that file was becoming one long scroll of
 * JSX. Copy lives in landing-content.ts.
 */
import { useCallback, useRef, useState } from 'react';
import { Button, Mono } from '../components/ui';
import {
  DECK_SLIDES,
  FAQ,
  HOW_IT_WORKS,
  PROOF_STATS,
  PULL_QUOTE,
  SAMPLE_REPORT,
  TRUST,
} from './landing-content';

/**
 * Placeholder art for the three deliverable cards, matching the prototype:
 * a bar chart for the deck, document lines for the report, and a score
 * histogram with a "YOU" marker for the instant scan. Pure CSS — the handoff
 * ships no raster assets and expects these replaced later.
 */
export function DeliverableArt({ kind }: { kind: 'deck' | 'report' | 'scan' }) {
  const frame = {
    aspectRatio: '16 / 9',
    borderRadius: 10,
    background: 'var(--tint)',
    border: '1px solid var(--line2)',
    padding: 14,
    display: 'flex',
    marginBottom: 16,
  } as const;

  if (kind === 'report') {
    return (
      <div aria-hidden="true" style={{ ...frame, flexDirection: 'column', gap: 7, justifyContent: 'center' }}>
        {[100, 78, 92, 64, 86].map((w, i) => (
          <div
            key={i}
            style={{
              height: i === 0 ? 5 : 4,
              width: `${w}%`,
              borderRadius: 3,
              background: i === 0 ? 'var(--ink2)' : 'var(--line)',
            }}
          />
        ))}
      </div>
    );
  }

  if (kind === 'scan') {
    // Histogram with the user's bar called out — the same idea as the
    // dashboard's real chart.
    const bars = [30, 52, 74, 96, 68, 44, 26];
    return (
      <div aria-hidden="true" style={{ ...frame, alignItems: 'flex-end', gap: 5 }}>
        {bars.map((h, i) => (
          <div key={i} style={{ flex: 1, position: 'relative' }}>
            {i === 3 && (
              <span
                style={{
                  position: 'absolute',
                  top: -18,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'var(--card)',
                  border: '1px solid var(--line)',
                  borderRadius: 4,
                  padding: '1px 5px',
                  font: '500 8px/1.4 var(--font-mono)',
                  color: 'var(--ink2)',
                }}
              >
                YOU
              </span>
            )}
            <div
              style={{
                height: h,
                borderRadius: 3,
                background: i === 3 ? 'var(--acc)' : 'var(--line)',
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div aria-hidden="true" style={{ ...frame, alignItems: 'flex-end', gap: 8 }}>
      {[46, 82, 60, 96].map((h, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            height: h,
            borderRadius: 4,
            background: i === 1 ? 'var(--acc)' : 'var(--line)',
          }}
        />
      ))}
    </div>
  );
}

export function HowItWorks() {
  return (
    <section
      id="how"
      style={{
        background: 'var(--tint)',
        borderTop: '1px solid var(--line2)',
        borderBottom: '1px solid var(--line2)',
        padding: '56px 0',
      }}
    >
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '0 24px' }}>
        <Mono>HOW IT WORKS</Mono>
        <div
          style={{
            display: 'grid',
            gap: 32,
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            marginTop: 28,
          }}
        >
          {HOW_IT_WORKS.map((step) => (
            <div key={step.n}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <span
                  style={{
                    width: 26,
                    height: 26,
                    flexShrink: 0,
                    borderRadius: '50%',
                    background: 'var(--acc)',
                    color: '#fff',
                    display: 'grid',
                    placeItems: 'center',
                    font: '600 13px/1 var(--font-ui)',
                  }}
                >
                  {step.n}
                </span>
                <span
                  style={{ height: 1, flex: 1, background: 'var(--line)' }}
                  aria-hidden="true"
                />
              </div>
              <h3
                style={{
                  margin: '0 0 7px',
                  fontSize: 18,
                  fontWeight: 600,
                  letterSpacing: '-.02em',
                }}
              >
                {step.title}
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: 14.5,
                  lineHeight: 1.6,
                  color: 'var(--muted)',
                }}
              >
                {step.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Stats and the founder quote share ONE white card, side by side — not two
 * stacked centred blocks. Measured off the rendered prototype: flex row,
 * gap 28, padding 24/28, radius 14, stats at 26px with 10px mono captions.
 */
export function ProofStrip() {
  return (
    <section style={{ maxWidth: 1080, margin: '0 auto', padding: '0 24px' }}>
      <div
        style={{
          display: 'flex',
          gap: 28,
          flexWrap: 'wrap',
          alignItems: 'center',
          border: '1px solid var(--line)',
          borderRadius: 14,
          background: 'var(--card)',
          padding: '24px 28px',
        }}
      >
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          {PROOF_STATS.map((s) => (
            <div key={s.label}>
              <div
                style={{
                  fontSize: 26,
                  fontWeight: 600,
                  letterSpacing: '-.03em',
                }}
              >
                {s.value}
              </div>
              <div style={{ marginTop: 7 }}>
                <Mono size={10} style={{ letterSpacing: '.05em' }}>
                  {s.label}
                </Mono>
              </div>
            </div>
          ))}
        </div>

        <blockquote style={{ flex: 1, minWidth: 260, margin: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: 15,
              lineHeight: 1.6,
              color: 'var(--ink2)',
            }}
          >
            {PULL_QUOTE.text}
          </p>
          <footer style={{ marginTop: 9 }}>
            <Mono size={10.5}>{PULL_QUOTE.attribution}</Mono>
          </footer>
        </blockquote>
      </div>
    </section>
  );
}

export function SampleReport() {
  const [tab, setTab] = useState<'deck' | 'report'>('deck');
  const [scrollProgress, setScrollProgress] = useState(0);
  const cleanupRef = useRef<(() => void) | null>(null);

  /**
   * The "SCROLL TO REVEAL" pill fades as the reader scrolls, so it stops
   * competing with the content it was pointing at.
   *
   * A CALLBACK REF, not useEffect + useRef: the scroll container is rendered
   * conditionally on `tab`, so on the commit that switches to 'report' the ref
   * is still null when an effect would run — the listener silently never
   * attached and the pill stayed at full opacity forever. A callback ref fires
   * exactly when the node mounts and unmounts.
   */
  const attachScroll = useCallback((el: HTMLDivElement | null) => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (!el) return;

    const onScroll = () => {
      const max = el.scrollHeight - el.clientHeight;
      setScrollProgress(max > 0 ? el.scrollTop / max : 0);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    cleanupRef.current = () => el.removeEventListener('scroll', onScroll);
    // Switching tabs remounts the container scrolled to the top.
    setScrollProgress(0);
  }, []);

  const fade = Math.max(0, 1 - scrollProgress * 2.2);

  return (
    <section id="sample" style={{ maxWidth: 1080, margin: '0 auto', padding: '76px 24px' }}>
      <h2
        style={{
          margin: 0,
          fontSize: 'clamp(28px, 4vw, 36px)',
          fontWeight: 600,
          letterSpacing: '-.03em',
          lineHeight: 1.12,
        }}
      >
        What you actually receive
      </h2>
      <p
        style={{
          margin: '12px 0 0',
          fontSize: 16.5,
          lineHeight: 1.6,
          color: 'var(--muted)',
          maxWidth: '58ch',
        }}
      >
        A real report for a real idea, published with the founder&apos;s
        permission. Scroll it to reveal the full sample.
      </p>

      {/* Segmented control */}
      <div
        role="tablist"
        aria-label="Sample deliverables"
        style={{
          display: 'inline-flex',
          gap: 3,
          background: 'var(--gutter)',
          borderRadius: 10,
          padding: 3,
          marginTop: 24,
        }}
      >
        {(
          [
            ['deck', 'Executive deck'],
            ['report', 'Detailed report'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            style={{
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 14,
              fontFamily: 'inherit',
              fontWeight: tab === key ? 600 : 500,
              cursor: 'pointer',
              background: tab === key ? 'var(--card)' : 'transparent',
              color: tab === key ? 'var(--ink)' : 'var(--muted)',
              boxShadow: tab === key ? '0 1px 2px rgba(24,23,32,.06)' : 'none',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'deck' ? (
        <div
          style={{
            display: 'grid',
            gap: 16,
            // Fixed 3-up, collapsing to 2 then 1 — the prototype's grid, not
            // an auto-fit that reflows to 4 on wide screens.
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            marginTop: 24,
          }}
        >
          {DECK_SLIDES.map((slide) => (
            <div
              key={slide.no}
              style={{
                border: '1px solid var(--line)',
                borderRadius: 12,
                overflow: 'hidden',
                background: 'var(--card)',
              }}
            >
              {/* Tinted 16:10 area with line-art standing in for slide content:
                  one dark title bar and two grey body lines. */}
              <div
                aria-hidden="true"
                style={{
                  aspectRatio: '16 / 10',
                  background: 'var(--tint)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  gap: 6,
                  padding: 14,
                }}
              >
                <div
                  style={{
                    height: 5,
                    width: '46%',
                    borderRadius: 3,
                    background: 'var(--ink2)',
                  }}
                />
                <div
                  style={{
                    height: 4,
                    width: '82%',
                    borderRadius: 3,
                    background: 'var(--line)',
                  }}
                />
                <div
                  style={{
                    height: 4,
                    width: '64%',
                    borderRadius: 3,
                    background: 'var(--line)',
                  }}
                />
              </div>
              {/* Number sits INLINE to the left of the title, in its own strip. */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '11px 14px',
                  borderTop: '1px solid var(--line2)',
                }}
              >
                <Mono size={10}>{slide.no}</Mono>
                <span style={{ fontSize: 14, letterSpacing: '-.01em' }}>
                  {slide.title}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ position: 'relative', marginTop: 24 }}>
          <div
            ref={attachScroll}
            style={{
              maxHeight: 520,
              overflowY: 'auto',
              background: 'var(--card)',
              border: '1px solid var(--line)',
              borderRadius: 16,
              padding: 28,
            }}
          >
            <SampleReportBody />
          </div>

          {/* White fade + pill, both fading out together as the reader scrolls. */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 1,
              right: 1,
              bottom: 1,
              height: 130,
              borderRadius: '0 0 16px 16px',
              background:
                'linear-gradient(to bottom, rgba(255,255,255,0), var(--card))',
              opacity: fade,
              transition: 'opacity .15s linear',
              pointerEvents: 'none',
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              bottom: 18,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'var(--ink)',
              color: '#fff',
              borderRadius: 20,
              padding: '7px 14px',
              opacity: fade,
              transition: 'opacity .15s linear',
              pointerEvents: 'none',
            }}
          >
            <Mono size={10} color="#fff">
              SCROLL TO REVEAL ↓
            </Mono>
          </div>
        </div>
      )}
    </section>
  );
}

function SampleReportBody() {
  const r = SAMPLE_REPORT;
  return (
    <article>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <Mono size={10}>{r.meta}</Mono>
        <span
          style={{
            background: 'var(--amber-soft)',
            border: '1px solid var(--amber-line)',
            borderRadius: 20,
            padding: '2px 8px',
            font: '500 9.5px/1.6 var(--font-mono)',
            letterSpacing: '.06em',
            color: 'var(--amber)',
          }}
        >
          SAMPLE
        </span>
      </div>

      <h3
        style={{
          margin: '10px 0 0',
          fontSize: 22,
          fontWeight: 600,
          letterSpacing: '-.02em',
          lineHeight: 1.3,
        }}
      >
        {r.title}
      </h3>

      {/* Verdict */}
      <div
        style={{
          background: 'var(--acc-soft)',
          border: '1px solid var(--acc-line)',
          borderRadius: 14,
          padding: 20,
          marginTop: 18,
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}
        >
          <span
            style={{
              fontSize: 40,
              fontWeight: 600,
              letterSpacing: '-.035em',
              lineHeight: 1,
              color: 'var(--acc-ink)',
            }}
          >
            {r.score}
          </span>
          <span style={{ fontSize: 15, color: 'var(--muted)' }}>/10</span>
          <span style={{ marginLeft: 4 }}>
            <Mono size={10}>OVERALL</Mono>
          </span>
        </div>
        <div
          style={{
            display: 'inline-block',
            background: 'var(--card)',
            borderRadius: 20,
            padding: '5px 12px',
            marginTop: 12,
          }}
        >
          <Mono size={10}>{r.percentile}</Mono>
        </div>
        <p
          style={{
            margin: '14px 0 0',
            fontSize: 15,
            lineHeight: 1.6,
            color: 'var(--ink2)',
          }}
        >
          {r.verdict}
        </p>
      </div>

      {/* Scores */}
      <div style={{ marginTop: 24 }}>
        <Mono size={10}>SCORES</Mono>
        <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
          {r.scores.map((s) => (
            <div key={s.label}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 13.5,
                  color: 'var(--ink2)',
                  marginBottom: 4,
                }}
              >
                <span>{s.label}</span>
                <span>{s.value}</span>
              </div>
              <div
                style={{
                  height: 7,
                  borderRadius: 4,
                  background: 'var(--gutter)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${(s.value / 10) * 100}%`,
                    height: '100%',
                    background: 'var(--acc)',
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Callouts */}
      <div
        style={{
          display: 'grid',
          gap: 14,
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          marginTop: 24,
        }}
      >
        <Callout
          label="BIGGEST RISK"
          body={r.risk}
          fg="var(--amber)"
          bg="var(--amber-soft)"
          line="var(--amber-line)"
        />
        <Callout
          label="STRONGEST SIGNAL"
          body={r.signal}
          fg="var(--green)"
          bg="var(--green-soft)"
          line="var(--green-line)"
        />
      </div>

      {/* Competitors */}
      <div style={{ marginTop: 24 }}>
        <Mono size={10}>COMPETITOR SNAPSHOT</Mono>
        <div
          style={{
            marginTop: 12,
            border: '1px solid var(--line)',
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          {r.competitors.map((c, i) => (
            <div
              key={c.name}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1.4fr 1.4fr',
                gap: 12,
                padding: '12px 14px',
                fontSize: 13,
                lineHeight: 1.5,
                background: i % 2 ? 'var(--hover)' : 'var(--card)',
                borderTop: i ? '1px solid var(--line2)' : 'none',
              }}
            >
              <span style={{ fontWeight: 600 }}>{c.name}</span>
              <span style={{ color: 'var(--ink2)' }}>{c.what}</span>
              <span style={{ color: 'var(--muted)' }}>{c.gap}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Experiments */}
      <div style={{ marginTop: 24 }}>
        <Mono size={10}>WHAT WE&apos;D VALIDATE NEXT</Mono>
        <ol style={{ listStyle: 'none', padding: 0, margin: '12px 0 0' }}>
          {r.validate.map((v, i) => (
            <li
              key={v}
              style={{ display: 'flex', gap: 12, marginTop: i ? 12 : 0 }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  borderRadius: '50%',
                  background: 'var(--acc-soft)',
                  border: '1px solid var(--acc-line)',
                  color: 'var(--acc-ink)',
                  display: 'grid',
                  placeItems: 'center',
                  font: '600 12px/1 var(--font-ui)',
                }}
              >
                {i + 1}
              </span>
              <span style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--ink2)' }}>
                {v}
              </span>
            </li>
          ))}
        </ol>
      </div>
    </article>
  );
}

function Callout({
  label,
  body,
  fg,
  bg,
  line,
}: {
  label: string;
  body: string;
  fg: string;
  bg: string;
  line: string;
}) {
  return (
    <div
      style={{
        background: bg,
        border: `1px solid ${line}`,
        borderRadius: 12,
        padding: 16,
      }}
    >
      <Mono size={10} color={fg}>
        {label}
      </Mono>
      <p
        style={{
          margin: '8px 0 0',
          fontSize: 13.5,
          lineHeight: 1.6,
          color: 'var(--ink2)',
        }}
      >
        {body}
      </p>
    </div>
  );
}

export function TrustStrip() {
  return (
    <section style={{ maxWidth: 1080, margin: '0 auto', padding: '0 24px 76px' }}>
      <div
        style={{
          background: 'var(--card)',
          border: '1px solid var(--line)',
          borderRadius: 14,
          padding: '22px 24px',
          display: 'flex',
          gap: 18,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: 9,
            background: 'var(--acc-soft)',
            border: '1px solid var(--acc-line)',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <rect
              x="3"
              y="7"
              width="10"
              height="7"
              rx="1.5"
              stroke="var(--acc)"
              strokeWidth="1.4"
            />
            <path
              d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2"
              stroke="var(--acc)"
              strokeWidth="1.4"
            />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h3
            style={{
              margin: '0 0 6px',
              fontSize: 16.5,
              fontWeight: 600,
              letterSpacing: '-.015em',
            }}
          >
            {TRUST.title}
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: 14.5,
              lineHeight: 1.6,
              color: 'var(--muted)',
            }}
          >
            {TRUST.body}
          </p>
        </div>
        {/* The link sits to the RIGHT of the copy, not stacked beneath it. */}
        <a
          href="/privacy"
          style={{
            flex: '0 0 auto',
            fontSize: 14,
            color: 'var(--ink2)',
            textDecoration: 'none',
          }}
        >
          {TRUST.link}
        </a>
      </div>
    </section>
  );
}

export function Faq() {
  // Second item open by default, matching the prototype.
  const [open, setOpen] = useState(1);

  // No tinted band and no narrow column: full 1080px, plain background,
  // divider rows only.
  return (
    <section
      id="faq"
      style={{ maxWidth: 1080, margin: '0 auto', padding: '64px 24px 76px' }}
    >
      <div>
        <Mono>FREQUENTLY ASKED</Mono>
        <div style={{ marginTop: 20 }}>
          {FAQ.map((item, i) => {
            const isOpen = open === i;
            return (
              <div
                key={item.q}
                style={{ borderTop: i ? '1px solid var(--line)' : 'none' }}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    background: 'none',
                    border: 'none',
                    padding: '18px 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: '500 16px/1.4 var(--font-ui)',
                    letterSpacing: '-.015em',
                    color: 'var(--ink)',
                  }}
                >
                  <span style={{ flex: 1 }}>{item.q}</span>
                  <span
                    aria-hidden="true"
                    style={{
                      fontSize: 20,
                      color: 'var(--muted)',
                      transform: isOpen ? 'rotate(45deg)' : 'none',
                      transition: 'transform .22s ease',
                      lineHeight: 1,
                    }}
                  >
                    +
                  </span>
                </button>
                {isOpen && (
                  <p
                    style={{
                      margin: '0 0 20px',
                      paddingRight: 36,
                      fontSize: 14.5,
                      lineHeight: 1.65,
                      color: 'var(--muted)',
                      animation: 'ibRise .22s ease both',
                    }}
                  >
                    {item.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * A tinted CARD with the line on the left and the button on the right — not a
 * centred hero band. Measured: padding 26, radius 14, 18px/500 text.
 */
export function ClosingCta({ onStart }: { onStart: () => void }) {
  return (
    <section style={{ maxWidth: 1080, margin: '0 auto', padding: '0 24px 44px' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 16,
          padding: 26,
          borderRadius: 14,
          background: 'var(--tint)',
          border: '1px solid var(--line2)',
        }}
      >
        <p
          style={{
            margin: 0,
            flex: 1,
            minWidth: 240,
            fontSize: 18,
            fontWeight: 500,
            letterSpacing: '-.02em',
          }}
        >
          Six minutes now, a real answer in two days.
        </p>
        <Button onClick={onStart} style={{ padding: '14px 22px', fontSize: 15.5 }}>
          Evaluate my idea
        </Button>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer
      style={{
        background: 'var(--tint)',
        borderTop: '1px solid var(--line2)',
        padding: '28px 0',
      }}
    >
      <div
        style={{
          maxWidth: 1080,
          margin: '0 auto',
          padding: '0 24px',
          display: 'flex',
          gap: 18,
          alignItems: 'center',
          flexWrap: 'wrap',
          fontSize: 13.5,
          color: 'var(--muted)',
        }}
      >
        <span>© 2026 IdeaBrief</span>
        <span style={{ flex: 1 }} />
        {[
          ['Privacy', '/privacy'],
          ['Terms', '/terms'],
          ['Contact', '/contact'],
        ].map(([label, href]) => (
          <a
            key={label}
            href={href}
            style={{ color: 'var(--muted)', textDecoration: 'none' }}
          >
            {label}
          </a>
        ))}
      </div>
    </footer>
  );
}
